import Joi from "joi";

// Password validation with strong requirements
const passwordSchema = Joi.string()
  .min(8)
  .max(50)
  .pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/)
  .required()
  .messages({
    "string.pattern.base":
      "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character",
    "string.min": "Password must be at least 8 characters long",
    "string.max": "Password cannot be longer than 50 characters",
  });

// Email validation
const emailSchema = Joi.string()
  .email({ tlds: { allow: true } })
  .lowercase()
  .max(255)
  .required()
  .messages({
    "string.email": "Please provide a valid email address",
    "string.max": "Email cannot be longer than 255 characters",
  });

// Device info validation (for social login and other flows)
const deviceInfoSchema = Joi.object({
  deviceId: Joi.string().required().messages({
    "any.required": "Device ID is required",
  }),
  deviceType: Joi.string().valid("ios", "android", "web").required().messages({
    "any.only": "Device type must be one of: ios, android, web",
    "any.required": "Device type is required",
  }),
  fcmToken: Joi.string().optional(),
  deviceName: Joi.string().max(100).optional(),
  appVersion: Joi.string().max(20).optional(),
});

// Device info validation for login (completely optional)
const deviceInfoOptionalSchema = Joi.object({
  deviceId: Joi.string().optional().allow("").messages({
    "string.base": "Device ID must be a string",
  }),
  deviceType: Joi.string().valid("ios", "android", "web").optional().messages({
    "any.only": "Device type must be one of: ios, android, web",
  }),
  userAgent: Joi.string().optional(),
  fcmToken: Joi.string().optional(),
  deviceName: Joi.string().max(100).optional(),
  appVersion: Joi.string().max(20).optional(),
});

// User registration validation
export const registerValidation = Joi.object({
  email: emailSchema,
  password: passwordSchema,
  role: Joi.string()
    .valid("admin", "school", "teacher", "student")
    .required()
    .messages({
      "any.only": "Role must be one of: admin, school, teacher, student",
      "any.required": "Role is required",
    }),
});

// Email verification validation (supports both signup and forgot password verification)
export const verifyEmailValidation = Joi.object({
  email: emailSchema,
  otp: Joi.string().length(6).pattern(/^\d+$/).required().messages({
    "string.length": "OTP must be exactly 6 digits",
    "string.pattern.base": "OTP must contain only numbers",
    "any.required": "OTP is required",
  }),
  // Fix: Add type parameter to distinguish between signup and forgot password verification
  type: Joi.string()
    .valid("signup", "forget")
    .optional()
    .default("signup")
    .messages({
      "any.only": "Type must be either 'signup' or 'forget'",
    }),
});

// Login validation
export const loginValidation = Joi.object({
  email: emailSchema,
  password: Joi.string().required().messages({
    "any.required": "Password is required",
  }),
  /** Must match the account role (e.g. teacher app → "teacher"). */
  userType: Joi.string()
    .valid("admin", "school", "teacher", "student")
    .required()
    .messages({
      "any.only": "userType must be one of: admin, school, teacher, student",
      "any.required": "userType is required",
    }),
  deviceInfo: deviceInfoOptionalSchema.optional(),
});

// Social login validation
export const socialLoginValidation = Joi.object({
  provider: Joi.string()
    .valid("google", "facebook", "apple")
    .required()
    .messages({
      "any.only": "Provider must be one of: google, facebook, apple",
      "any.required": "Provider is required",
    }),
  providerId: Joi.string().required().messages({
    "any.required": "Provider ID is required",
  }),
  email: emailSchema,
  name: Joi.string().max(100).optional(),
  profileImage: Joi.string().uri().optional(),
  role: Joi.string()
    .valid("admin", "school", "teacher", "student")
    .required()
    .messages({
      "any.only": "Role must be one of: admin, school, teacher, student",
      "any.required": "Role is required",
    }),
  deviceInfo: deviceInfoSchema.required(),
});

// Forgot password validation
export const forgotPasswordValidation = Joi.object({
  email: emailSchema,
});

// Reset password validation (with OTP - no authentication required)
export const resetPasswordValidation = Joi.object({
  email: emailSchema,
  otp: Joi.string()
    .length(6)
    .pattern(/^\d{6}$/)
    .required()
    .messages({
      "string.length": "OTP must be exactly 6 digits",
      "string.pattern.base": "OTP must contain only numbers",
      "any.required": "OTP is required",
    }),
  newPassword: passwordSchema.label("New password"),
});

// Change password validation
export const changePasswordValidation = Joi.object({
  currentPassword: Joi.string().required().messages({
    "any.required": "Current password is required",
  }),
  newPassword: passwordSchema.label("New password"),
});

// Refresh token validation
export const refreshTokenValidation = Joi.object({
  refreshToken: Joi.string().required().messages({
    "any.required": "Refresh token is required",
  }),
});

// Email availability validation
export const emailAvailabilityValidation = Joi.object({
  email: emailSchema,
});

// School code validation
export const schoolIdValidation = Joi.object({
  schoolId: Joi.string()
    .pattern(/^[0-9a-fA-F]{24}$/)
    .required()
    .messages({
      "string.pattern.base": "School ID must be a valid MongoDB ObjectId",
      "any.required": "School ID is required",
    }),
});

// Resend OTP validation
export const resendOTPValidation = Joi.object({
  email: emailSchema,
  type: Joi.string()
    .valid("email_verification", "password_reset")
    .required()
    .messages({
      "any.only": "Type must be either email_verification or password_reset",
      "any.required": "Type is required",
    }),
});
